class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  onSubmitHandler(evt) {
    // Check if this is a product with photo upload
    const hasPhotoUpload = this.form.dataset.hasPhotoUpload === 'true';
    const photoInputsContainer = document.getElementById('photoInputsContainer');
    const hasPhotos = photoInputsContainer && photoInputsContainer.querySelectorAll('input[type="file"]').length > 0;

    // For products with photo uploads, use native form submission
    if (hasPhotoUpload && hasPhotos) {
      // Validate photos are present
      const photoUpload = document.getElementById('photoUpload');
      if (photoUpload) {
        const photoGrid = document.getElementById('photoGrid');
        const photoCount = photoGrid ? photoGrid.querySelectorAll('.photo-upload__item').length : 0;

        if (photoCount === 0) {
          evt.preventDefault();
          this.handleErrorMessage('Please upload at least one photo before adding to cart.');
          return;
        }

        // Move photo inputs into the form
        const inputs = photoInputsContainer.querySelectorAll('input[type="file"]');
        inputs.forEach(input => {
          const clone = input.cloneNode(true);
          this.form.appendChild(clone);
        });

        // Add photo count property
        const countInput = document.createElement('input');
        countInput.type = 'hidden';
        countInput.name = 'properties[Custom Photos Count]';
        countInput.value = photoCount;
        this.form.appendChild(countInput);

        // CRITICAL: Set quantity to photo count so Shopify charges correctly
        // 2 photos = quantity 2 = charged for 2 items
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        if (quantityInput) {
          quantityInput.value = photoCount;
        } else {
          // Create quantity input if it doesn't exist
          const qtyInput = document.createElement('input');
          qtyInput.type = 'hidden';
          qtyInput.name = 'quantity';
          qtyInput.value = photoCount;
          this.form.appendChild(qtyInput);
        }
      }

      // Show loading state
      this.submitButton.classList.add('loading');
      this.submitButton.textContent = 'Adding...';

      // Let the form submit natively (don't prevent default)
      return;
    }

    // Standard AJAX submission for products without photo upload
    evt.preventDefault();

    if (this.submitButton.classList.contains('loading')) return;

    this.handleErrorMessage();
    this.submitButton.classList.add('loading');
    this.submitButton.textContent = 'Adding...';

    // Get the variant ID from the form
    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;
    const sellingPlan = formData.get('selling_plan');

    // Build cart item
    const cartItem = {
      id: parseInt(variantId),
      quantity: parseInt(quantity)
    };

    // Add selling_plan if subscription selected
    if (sellingPlan) {
      cartItem.selling_plan = parseInt(sellingPlan);
    }

    // Use same approach as bundle selector - JSON body
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [cartItem]
      })
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to add to cart');
        return response.json();
      })
      .then((response) => {
        // Redirect to cart page (same as bundle selector)
        window.location.href = '/cart';
      })
      .catch((e) => {
        console.error('Add to cart error:', e);
        this.handleErrorMessage('Error adding to cart. Please try again.');
        this.submitButton.classList.remove('loading');
        this.submitButton.textContent = 'ADD TO CART';
      });
  }

  handleErrorMessage(errorMessage = false) {
    const errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
    const errorElement = this.querySelector('.product-form__error-message');

    if (!errorMessageWrapper || !errorElement) return;

    if (errorMessage) {
      errorElement.textContent = errorMessage;
      errorMessageWrapper.toggleAttribute('hidden', false);
    } else {
      errorMessageWrapper.toggleAttribute('hidden', true);
    }
  }

  get submitButton() {
    return this.querySelector('.btn-add-to-cart');
  }
}

customElements.define('product-form', ProductForm);

// Variant selector functionality
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.querySelector('.product-oleg__gallery');
    if (!mediaGallery) return;

    const newMedia = mediaGallery.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    if (!newMedia) return;

    const activeMedia = mediaGallery.querySelector('.product-oleg__media-item.active');
    if (activeMedia) activeMedia.classList.remove('active');
    newMedia.classList.add('active');
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById('Share');
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant?.id;
    const sectionId = this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${sectionId}`);
        const source = html.getElementById(`price-${sectionId}`);
        const skuSource = html.getElementById(`Sku-${sectionId}`);
        const skuDestination = document.getElementById(`Sku-${sectionId}`);
        const inventorySource = html.getElementById(`Inventory-${sectionId}`);
        const inventoryDestination = document.getElementById(`Inventory-${sectionId}`);

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${sectionId}`);
        if (price) price.classList.remove('visibility-hidden');
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;

    const addButton = productForm.querySelector('.btn-add-to-cart');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButton.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButton.textContent = 'ADD TO CART';
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;

    const addButton = productForm.querySelector('.btn-add-to-cart');
    if (!addButton) return;

    addButton.textContent = 'Sold out';
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);